import { useLayer } from '@/layers/useLayer.tsx';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { useStaffNotes } from '@/sidePanel/useStaffNotes.ts';
import { LayerOpenProps } from '@/layers/LayerProvider.tsx';

export interface LayerUiEventConfig extends LayerOpenProps {
  ref?: string;
}

export interface FunctionUiEventConfig {
  params?: boolean[];
  name?: 'validateSlide';
}

export interface NavUiEventConfig {
  target?: 'cancel' | 'reset' | 'next' | 'prev' | 'financialContribution' | 'scanGrid' | 'feedback';
  delay?: number;
}

export interface UiEventConfig extends LayerUiEventConfig, FunctionUiEventConfig, NavUiEventConfig {
  type: 'nav' | 'layer' | 'function';
}

export const useUiEvent = () => {
  const layerControl = useLayer();
  const navControl = useNavigation();
  const staffNoteControl = useStaffNotes();

  const cancel = () => {
    if (!navControl.navControl.hasCancel) {
      return;
    }
  };

  const reset = () => {
    /** clear internal data */
    staffNoteControl.clearStaffNotes();
    navControl.reset();
  };

  const handleUINavigation = (target: NavUiEventConfig['target']) => {
    switch (target) {
      case 'cancel':
        return cancel();

      case 'reset':
        return reset();

      case 'next':
        navControl.next();
        break;

      case 'prev':
        navControl.prev();
        break;

      case 'financialContribution':
        navControl.gotoSlide('financialContribution');
        break;

      case 'scanGrid':
        navControl.gotoSlide('scanGrid');
        break;

      case 'feedback':
        navControl.gotoSlide('feedback');
        break;

      default:
        throw new Error('Unsupported navigation target: ' + target);
    }
  };

  const handleUiEvent = (config: UiEventConfig) => {
    switch (config.type) {
      case 'nav': {
        const delay = parseInt(`${config.delay ?? 0}`) * 1000;
        window.setTimeout(() => {
          handleUINavigation(config.target);
        }, delay);
        break;
      }

      case 'layer':
        layerControl.showLayer(config);
        break;

      case 'function':
        {
          const validateSlide = (valid: boolean) => {
            navControl.setSlideValid(valid);
            layerControl.closeLayer();
            return true;
          };

          if (config.name !== 'validateSlide') {
            throw new Error('Unsupported UI handler function' + JSON.stringify(config));
          }
          validateSlide((config.params ?? [])[0]); // This really has only one param.
        }
        break;

      default:
        throw new Error('Unsupported UI event: ' + config.type);
    }
  };

  return { handleUiEvent };
};
